import { useStatsigClient } from '@statsig/react-bindings';

import {
  QUESTION_TYPES,
  QUESTION_TYPES_CAN_CARRY_FORWARD,
  QUESTION_TYPES_CARRY_FORWARD_SOURCE,
} from '@api/instant_survey';
import type { CarryForwardLogic, Question, QuestionAnswer } from '@api/instant_survey/types';
import { GATES } from '@constants/statsig';
import { Card, Checkbox, OverlayTriggerTooltip, ThumbsDownIcon, ThumbsUpIcon } from '@utilities';

import AnswersFormat from './components/AnswersFormat';
import CarryForward from './components/CarryForward';
import EditableText from '../EditableText';
import MaxAnswers from './components/MaxAnswers';
import MaxTotal from './components/MaxTotal';
import NumberOfSets from './components/NumberOfSets';
import OptionsPerSet from './components/OptionsPerSet';
import QuestionOrder from './components/QuestionOrder';
import ResponseLength from './components/ResponseLength';
import ValuesBetween from './components/ValuesBetween';

import { QUESTION_MAX_DIFF_LABEL_LENGTH, VALIDATION_MESSAGES } from '../../utilities/constants';
import { isAttentionCheckQuestion } from '../../../../../../utilities/helpers';

import styles from './_index.module.scss';

export interface QuestionPropertiesProps {
  carryForwardLogic: null | Partial<CarryForwardLogic>;
  onDeleteAnswers: (answers: QuestionAnswer[]) => void;
  onDeleteSubQuestions: (subQuestions: Question[]) => void;
  onUpdateQuestion: (change: Partial<Question>) => void;
  onUpdateQuestionProperties: (change: Partial<Question['properties']>) => void;
  question: Question;
  questionsBeforeCurrent: Question[];
  setCarryForwardLogic: React.Dispatch<React.SetStateAction<null | Partial<CarryForwardLogic>>>;
}

const QuestionProperties = ({
  carryForwardLogic,
  onDeleteAnswers,
  onDeleteSubQuestions,
  onUpdateQuestion,
  onUpdateQuestionProperties,
  question,
  questionsBeforeCurrent,
  setCarryForwardLogic,
}: QuestionPropertiesProps) => {
  const { client } = useStatsigClient();
  const isCalypso = client.checkGate(GATES.USE_CALYPSO_AS_SURVEY_PROVIDER);
  const isCarryForwardEnabled = client.checkGate(GATES.CARRY_FORWARD);
  const isCarryForwardIntoGridEnabled = client.checkGate(GATES.CARRY_FORWARD_INTO_GRID);

  const { properties, type } = question;

  const isCheckbox = type === QUESTION_TYPES.CHECKBOX.value;
  const isContSum = type === QUESTION_TYPES.CONTSUM.value;
  const isGrid = type === QUESTION_TYPES['TABLE-RADIO'].value;
  const isInstructions = type === QUESTION_TYPES.INSTRUCTIONS.value;
  const isMaxDiff = type === QUESTION_TYPES.MAX_DIFF.value;
  const isNumeric = type === QUESTION_TYPES.NUMERIC.value;
  const isOpenEnd = type === QUESTION_TYPES.ESSAY.value;
  const isRadio = type === QUESTION_TYPES.RADIO.value;
  const isRank = type === QUESTION_TYPES['RANK-DRAGDROP'].value;
  const isTurf = type === QUESTION_TYPES.TURF.value;
  const isVanWestendorp = type === QUESTION_TYPES.VAN_WESTENDORP.value;
  const isVideo = type === QUESTION_TYPES.VIDEO.value;

  const canCarryForward =
    isCalypso &&
    isCarryForwardEnabled &&
    (!isGrid || isCarryForwardIntoGridEnabled) &&
    QUESTION_TYPES_CAN_CARRY_FORWARD.includes(type) &&
    questionsBeforeCurrent.some(
      (question) =>
        QUESTION_TYPES_CARRY_FORWARD_SOURCE.includes(question.type) &&
        !isAttentionCheckQuestion(question) &&
        !question.isScreener
    );
  const canMaxAnswers = isCheckbox || isTurf || (isRank && isCalypso);
  const canRandomizeOptions = isCheckbox || isContSum || isRadio || isRank || isTurf;

  const canSetLength = isOpenEnd && isCalypso;

  if (isInstructions || isVideo || (isOpenEnd && !isCalypso)) return null;

  return (
    <Card className={styles['question-properties']}>
      {canRandomizeOptions && (
        <div className={styles['question-properties-option']}>
          <Checkbox
            isChecked={properties.randomizeOptions}
            label="Randomize order"
            onChange={() =>
              onUpdateQuestionProperties({ randomizeOptions: !properties.randomizeOptions })
            }
            value={properties.randomizeOptions}
          />
        </div>
      )}
      {isGrid && (
        <div className={styles['question-properties-option']}>
          <Checkbox
            isChecked={properties.randomizeSubQuestions}
            label="Randomize question rows order"
            onChange={() =>
              onUpdateQuestionProperties({
                randomizeSubQuestions: !properties.randomizeSubQuestions,
              })
            }
            value={properties.randomizeSubQuestions}
          />
        </div>
      )}

      {canMaxAnswers && (
        <div className={styles['question-properties-option']}>
          <MaxAnswers question={question} updateQuestionProperties={onUpdateQuestionProperties} />
        </div>
      )}

      {(isContSum || isNumeric || isVanWestendorp) && (
        <div className={styles['question-properties-option']}>
          <AnswersFormat
            question={question}
            updateQuestionProperties={onUpdateQuestionProperties}
          />
          <ValuesBetween
            question={question}
            updateQuestionProperties={onUpdateQuestionProperties}
          />
          {isContSum && (
            <MaxTotal question={question} updateQuestionProperties={onUpdateQuestionProperties} />
          )}
          {isVanWestendorp && (
            <QuestionOrder
              question={question}
              updateQuestionProperties={onUpdateQuestionProperties}
            />
          )}
        </div>
      )}

      {isMaxDiff && (
        <div className={styles['question-properties-option']}>
          <div className={styles['question-properties-label-wrapper']}>
            <OverlayTriggerTooltip content="Best Label">
              <ThumbsUpIcon />
            </OverlayTriggerTooltip>
            {/* @ts-ignore */}
            <EditableText
              aria-label="Best Label"
              error={
                properties.bestLabel && properties.bestLabel.length > QUESTION_MAX_DIFF_LABEL_LENGTH
                  ? VALIDATION_MESSAGES.MAX_DIFF_BEST_LABEL_LENGTH_ERROR
                  : null
              }
              maxLength={QUESTION_MAX_DIFF_LABEL_LENGTH}
              onChange={(value: string) => onUpdateQuestionProperties({ bestLabel: value })}
              value={properties.bestLabel}
            />
          </div>
          <div className={styles['question-properties-label-wrapper']}>
            <OverlayTriggerTooltip content="Worst Label">
              <ThumbsDownIcon />
            </OverlayTriggerTooltip>
            {/* @ts-ignore */}
            <EditableText
              aria-label="Worst Label"
              error={
                properties.worstLabel &&
                properties.worstLabel.length > QUESTION_MAX_DIFF_LABEL_LENGTH
                  ? VALIDATION_MESSAGES.MAX_DIFF_WORST_LABEL_LENGTH_ERROR
                  : null
              }
              maxLength={QUESTION_MAX_DIFF_LABEL_LENGTH}
              onChange={(value: string) => onUpdateQuestionProperties({ worstLabel: value })}
              value={properties.worstLabel}
            />
          </div>
          <span className={styles['question-properties-divider']} />
          <OptionsPerSet
            question={question}
            setOptionsPerSet={(value: number) =>
              onUpdateQuestionProperties({ optionsPerSet: value })
            }
          />
          <span className={styles['question-properties-divider']} />
          <NumberOfSets
            question={question}
            setNumberOfSets={(value: number) => onUpdateQuestionProperties({ numberOfSets: value })}
          />
        </div>
      )}

      {canSetLength && (
        <div className={styles['question-properties-option']}>
          <ResponseLength
            question={question}
            updateQuestionProperties={onUpdateQuestionProperties}
          />
        </div>
      )}

      {canCarryForward && (
        <div className={styles['question-properties-option']}>
          <CarryForward
            carryForwardLogic={carryForwardLogic}
            onDeleteAnswers={onDeleteAnswers}
            onDeleteSubQuestions={onDeleteSubQuestions}
            onUpdateQuestion={onUpdateQuestion}
            question={question}
            questionsBeforeCurrent={questionsBeforeCurrent}
            setCarryForwardLogic={setCarryForwardLogic}
          />
        </div>
      )}
    </Card>
  );
};

export default QuestionProperties;
